// PitchDeckBlocks.tsx — MJOS-028, refreshed MJOS-063
// (CostSimSlide: Sonnet emphasized via tight green pill; Haiku plain ink)
//
// Block library for the MAX Security Eyal pitch deck.
//
// Reference: /public/blueprints/max-security-eyal-pitch-deck.html (578 lines,
// archived). The reference is a stand-alone HTML file with absolutely-positioned
// slides, JS-driven slide navigation, keyboard arrows, dot indicators.
//
// MJOS-063 fixes (in order):
//   * Nav uses `position: absolute` so it stays CONTAINED within the deck
//     wrapper (PitchDocBlueprintPage's `absolute inset-0` box). Was `fixed`,
//     which broke out past the voice-panel column.
//   * Progress bar + play button match the voice-feed look (shared Slider
//     component, green-600 button, lucide Play/Pause).
//   * Progress is driven by a `requestAnimationFrame` loop polling
//     `audio.currentTime` (~60fps smooth) — NOT the audio element's
//     `timeupdate` event (~4Hz steppy). This mirrors the
//     `myjarvis-architecture.html` reference deck behavior.
//   * Auto-advance is a simple `autoPlayRef` boolean:
//       - first user play click ⇒ autoPlayRef=true
//       - on `ended`, if autoPlayRef && there's a next slide, setActive(+1)
//       - the active-change effect plays the new audio when autoPlayRef
//         is true
//       - manual dot click resets autoPlayRef=false (user has taken over)
//     Pausing mid-slide doesn't disable autoPlay — resume continues to
//     chain. Last slide ends naturally and the player resets.

import React, { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import type { BlockConfig } from "./BlockRenderer";

// ── Palette — copied from reference HTML's :root variables ────────────────
const T = {
  bg: "#F2F7FD",
  bg2: "#E5EEF9",
  ink: "#1C1917",
  ink2: "#57534E",
  ink3: "#A8A29E",
  sky: "#7AB4F4",
  skyDark: "#3B82C8",
  skySoft: "#D2E4FC",
  line: "#D0DEF0",
  accent: "#2A6AAC",
  green: "#2A7A4B",
  greenSoft: "#E8F3EC",
  amber: "#B87333",
  amberSoft: "#FCEFD9",
  blue: "#3B6BA5",
  blueSoft: "#E3EDF7",
  plum: "#8E4585",
  plumSoft: "#F3E6F2",
  red: "#B23A3A",
  redSoft: "#FBE5E5",
  hubspot: "#FF7A59",
  hubspotSoft: "#FFE7DF",
  outlook: "#0078D4",
  outlookSoft: "#DAEAF7",
  monday: "#FFCB00",
  mondaySoft: "#FFF4CC",
  mondayInk: "#6B5B00",
  white: "#FFFFFF",
} as const;

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif";

// ── Slide container styles ─────────────────────────────────────────────────
const slideContainerStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  padding: "6vh 8vw 12vh",
  opacity: 0,
  transform: "translateY(20px)",
  transition:
    "opacity 0.6s cubic-bezier(0.22,1,0.36,1), transform 0.6s cubic-bezier(0.22,1,0.36,1)",
  pointerEvents: "none",
  overflowY: "auto",
};

const slideContainerActiveStyle: React.CSSProperties = {
  opacity: 1,
  transform: "translateY(0)",
  pointerEvents: "auto",
};

// ── Block prop types ───────────────────────────────────────────────────────
type CoverProps = {
  mark: string;
  titleLine1: string;
  titleAccent: string;
  sub: string;
  micro: string;
};

type ArchSysBox = {
  variant: "hubspot" | "outlook" | "monday" | "dash" | "output";
  tag: string;
  name: string;
  desc: string;
};

type ChipVariant = "default" | "blue" | "green" | "amber" | "plum" | "red";

type ArchSlideProps = {
  chipText: string;
  chipVariant: ChipVariant;
  title: string;
  sources: ArchSysBox[];
  middle: ArchSysBox;
  outputs: ArchSysBox[];
};

type UseCaseFlowNode = {
  variant: "hubspot" | "outlook" | "monday" | "dash";
  label: string;
};

type UseCaseCard = {
  metric: boolean;
  tag: string;
  ttl: string;
  body?: string;
};

type UseCaseSlideProps = {
  chipText: string;
  chipVariant: ChipVariant;
  title: string;
  flowNodes: UseCaseFlowNode[];
  cards: UseCaseCard[];
};

// ── Helpers ────────────────────────────────────────────────────────────────
function renderTitleLines(title: string, accentLine?: string) {
  const lines = title.split("|").map((l) => l.trim());
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {accentLine === line ? (
        <span style={{ color: T.skyDark, fontWeight: 500, fontStyle: "italic" }}>{line}</span>
      ) : (
        line
      )}
      {i < lines.length - 1 && <br />}
    </React.Fragment>
  ));
}

function chipStyle(variant: ChipVariant): React.CSSProperties {
  const map: Record<string, [string, string]> = {
    default: [T.skySoft, T.accent],
    blue: [T.blueSoft, T.blue],
    green: [T.greenSoft, T.green],
    amber: [T.amberSoft, T.amber],
    plum: [T.plumSoft, T.plum],
    red: [T.redSoft, T.red],
  };
  const [bg, color] = map[variant] ?? map.default;
  return {
    display: "inline-block",
    padding: "5px 12px",
    background: bg,
    color,
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 500,
    marginBottom: 18,
    letterSpacing: 0.3,
    // Keep the eyebrow chip on ONE line — in PDF export a constrained width
    // was wrapping multi-word chips (e.g. "Sales · automation candidates").
    whiteSpace: "nowrap",
  };
}

function sysBoxStyle(variant: ArchSysBox["variant"]): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: "18px 20px",
    borderRadius: 14,
    background: T.white,
    border: `1px solid ${T.line}`,
    position: "relative",
  };
  if (variant === "dash") {
    return {
      ...base,
      background: "linear-gradient(135deg, #3B82C8, #8E4585)",
      border: "1px solid transparent",
      color: T.white,
    };
  }
  if (variant === "output") {
    return { ...base, background: T.skySoft, border: "1px solid transparent" };
  }
  return base;
}

function sysTagStyle(variant: ArchSysBox["variant"]): React.CSSProperties {
  const base: React.CSSProperties = {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    padding: "3px 9px",
    borderRadius: 6,
  };
  switch (variant) {
    case "hubspot":
      return { ...base, background: T.hubspotSoft, color: T.hubspot };
    case "outlook":
      return { ...base, background: T.outlookSoft, color: T.outlook };
    case "monday":
      return { ...base, background: T.mondaySoft, color: T.mondayInk };
    case "dash":
      return { ...base, background: "rgba(255,255,255,0.2)", color: T.white };
    case "output":
      return { ...base, background: T.accent, color: T.white };
  }
}

function fnodeStyle(variant: UseCaseFlowNode["variant"]): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: "10px 14px",
    borderRadius: 10,
    fontSize: 12.5,
    fontWeight: 600,
  };
  switch (variant) {
    case "hubspot":
      return { ...base, background: T.hubspotSoft, color: T.hubspot };
    case "outlook":
      return { ...base, background: T.outlookSoft, color: T.outlook };
    case "monday":
      return { ...base, background: T.mondaySoft, color: T.mondayInk };
    case "dash":
      return {
        ...base,
        background: `linear-gradient(135deg, ${T.skyDark}, ${T.plum})`,
        color: T.white,
      };
  }
}

// ── Deck wrapper ──────────────────────────────────────────────────────────
function Deck({
  children,
  audioBase,
  rtl,
}: {
  children?: React.ReactNode;
  audioBase?: string;
  rtl?: boolean;
}) {
  const [active, setActive] = useState(0);
  const [count, setCount] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // True once the user has clicked play. Carries play state across slides
  // so audio chains automatically. Manual dot navigation flips it off.
  const autoPlayRef = useRef(false);

  // Count slide children + apply active attribute.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const w = wrapperRef.current;
    if (!w) return;
    const slides = w.querySelectorAll<HTMLElement>("[data-slide]");
    setCount((prev) => (prev === slides.length ? prev : slides.length));
    slides.forEach((el, i) => {
      el.setAttribute("data-active", i === active ? "true" : "false");
      Object.assign(el.style, i === active ? slideContainerActiveStyle : {});
      if (i !== active) {
        el.style.opacity = "0";
        el.style.transform = "translateY(20px)";
        el.style.pointerEvents = "none";
      }
    });
  });

  // Load audio for the active slide. If autoPlay is on, play it.
  useEffect(() => {
    if (!audioBase) return;
    const audio = audioRef.current;
    if (!audio) return;
    const padded = String(active + 1).padStart(2, "0");
    audio.src = `${audioBase}/slide-${padded}.mp3`;
    audio.load();
    setCurrentTime(0);
    setDuration(0);
    if (autoPlayRef.current) {
      void audio.play().catch(() => undefined);
    }
  }, [active, audioBase]);

  // rAF-driven progress polling — smooth at 60fps. Same pattern as
  // VoicePlayerInline + the reference HTML decks.
  useEffect(() => {
    if (!playing) return;
    const audio = audioRef.current;
    if (!audio) return;
    let raf = 0;
    const tick = () => {
      setCurrentTime(audio.currentTime);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  // Keyboard: arrows navigate; spacebar toggles play (audio mode only)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        setActive((c) => Math.min(c + 1, count - 1));
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setActive((c) => Math.max(c - 1, 0));
      }
      if (e.key === "Home") setActive(0);
      if (e.key === "End") setActive(Math.max(0, count - 1));
      if (audioBase && e.key === " ") {
        e.preventDefault();
        togglePlay();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, audioBase]);

  function formatTime(seconds: number): string {
    if (!isFinite(seconds) || seconds < 0) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  }

  function togglePlay() {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      autoPlayRef.current = true;
      void audio.play().catch(() => undefined);
    } else {
      audio.pause();
    }
  }

  function onSeek(v: number[]) {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const t = Math.max(0, Math.min(duration, v[0]));
    audio.currentTime = t;
    setCurrentTime(t);
  }

  return (
    <div
      ref={wrapperRef}
      dir={rtl ? "rtl" : "ltr"}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        background: T.bg,
        color: T.ink,
        fontFamily: rtl
          ? "'Heebo', 'Assistant', 'Rubik', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
          : FONT,
        letterSpacing: rtl ? 0 : "-0.011em",
      }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap'); @import url('https://fonts.googleapis.com/css2?family=Heebo:wght@300;400;500;600;700;800&display=swap');`}</style>

      {children}

      {audioBase && (
        <audio
          ref={audioRef}
          preload="auto"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
          onEnded={() => {
            if (autoPlayRef.current && active < count - 1) {
              setActive(active + 1);
            }
          }}
        />
      )}

      {/* Nav — CONTAINED within the deck wrapper via position:absolute */}
      <div
        className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-between gap-4 border-t px-6 py-3"
        style={{
          background: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderTopColor: T.line,
          boxShadow: "0 -2px 16px rgba(28,25,23,0.04)",
        }}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span
            className="shrink-0 whitespace-nowrap text-xs font-semibold tabular-nums"
            style={{ color: T.ink2, minWidth: 48 }}
          >
            {active + 1} / {count}
          </span>

          {audioBase && (
            <>
              <button
                type="button"
                onClick={togglePlay}
                aria-label={playing ? "Pause" : "Play"}
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors ${
                  playing
                    ? "bg-green-600 text-white"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                }`}
              >
                {playing ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Play className="ml-0.5 h-4 w-4" />
                )}
              </button>

              <span className="min-w-[40px] text-xs text-gray-500 tabular-nums">
                {formatTime(currentTime)}
              </span>

              <Slider
                value={[currentTime]}
                max={duration || 100}
                step={0.1}
                onValueChange={onSeek}
                className="flex-1"
              />

              <span className="min-w-[40px] text-xs text-gray-500 tabular-nums">
                {formatTime(duration)}
              </span>
            </>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <button
            type="button"
            onClick={() => setActive((c) => Math.max(0, c - 1))}
            disabled={active === 0}
            style={navBtnStyle(active === 0)}
            aria-label="Previous slide"
          >
            ←
          </button>
          <div className="flex gap-1.5">
            {Array.from({ length: count }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  // Manual dot click = user has taken over; stop auto-advance.
                  autoPlayRef.current = false;
                  setActive(i);
                }}
                aria-label={`Slide ${i + 1}`}
                style={dotStyle(i === active)}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setActive((c) => Math.min(count - 1, c + 1))}
            disabled={active === count - 1}
            style={navBtnStyle(active === count - 1)}
            aria-label="Next slide"
          >
            →
          </button>
        </div>
      </div>
    </div>
  );
}

function navBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    background: "none",
    border: "none",
    cursor: disabled ? "default" : "pointer",
    width: 34,
    height: 34,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: T.ink,
    fontSize: 18,
    transition: "background 0.2s ease",
    opacity: disabled ? 0.25 : 1,
  };
}

function dotStyle(isActive: boolean): React.CSSProperties {
  return {
    width: isActive ? 20 : 7,
    height: 7,
    borderRadius: isActive ? 4 : "50%",
    background: isActive ? T.skyDark : T.ink3,
    opacity: isActive ? 1 : 0.3,
    cursor: "pointer",
    transition: "all 0.3s ease",
    flexShrink: 0,
    border: "none",
    padding: 0,
  };
}

// ── Slide block components ─────────────────────────────────────────────────

function CoverSlide({ mark, titleLine1, titleAccent, sub, micro }: CoverProps) {
  return (
    <section data-slide="cover" style={slideContainerStyle}>
      <div
        style={{
          fontSize: 22,
          fontWeight: 600,
          color: T.skyDark,
          marginBottom: 48,
        }}
      >
        {mark}
      </div>
      <h1
        style={{
          fontSize: "clamp(58px, 8.5vw, 120px)",
          fontWeight: 600,
          lineHeight: 1.02,
          letterSpacing: "-0.03em",
          margin: 0,
        }}
      >
        {titleLine1}
        <br />
        <span style={{ color: T.skyDark, fontWeight: 500, fontStyle: "italic" }}>
          {titleAccent}
        </span>
      </h1>
      <div
        style={{
          marginTop: 24,
          fontSize: "clamp(16px, 1.3vw, 20px)",
          color: T.ink2,
          fontWeight: 400,
          maxWidth: "56ch",
          lineHeight: 1.5,
        }}
      >
        {sub}
      </div>
      <div
        style={{
          fontSize: 12,
          color: T.ink3,
          letterSpacing: "0.02em",
          marginTop: 28,
        }}
      >
        {micro}
      </div>
    </section>
  );
}

function ArchitectureSlide({
  chipText,
  chipVariant,
  title,
  sources,
  middle,
  outputs,
}: ArchSlideProps) {
  return (
    <section data-slide="architecture" style={slideContainerStyle}>
      <div style={chipStyle(chipVariant)}>{chipText}</div>
      <h2
        style={{
          fontSize: "clamp(28px, 3.6vw, 48px)",
          fontWeight: 600,
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          maxWidth: "22ch",
          margin: 0,
        }}
      >
        {renderTitleLines(title)}
      </h2>
      <div
        style={{
          marginTop: 32,
          display: "grid",
          gridTemplateRows: "auto auto auto auto auto",
          gap: 14,
          maxWidth: 1080,
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }}>
          {(sources || []).map((s, i) => (
            <SysBox key={i} box={s} />
          ))}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            color: T.skyDark,
            fontSize: 22,
            lineHeight: 1,
          }}
        >
          ↓ &nbsp; ↓ &nbsp; ↓
        </div>
        <div style={{ maxWidth: 540, margin: "0 auto", width: "100%" }}>
          <SysBox box={middle} />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            color: T.skyDark,
            fontSize: 22,
            lineHeight: 1,
          }}
        >
          ↓ &nbsp; ↓
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 18,
            maxWidth: 700,
            margin: "0 auto",
          }}
        >
          {(outputs || []).map((s, i) => (
            <SysBox key={i} box={s} />
          ))}
        </div>
      </div>
    </section>
  );
}

function UseCaseSlide({
  chipText,
  chipVariant,
  title,
  flowNodes,
  cards,
}: UseCaseSlideProps) {
  return (
    <section data-slide="usecase" style={slideContainerStyle}>
      <div style={chipStyle(chipVariant)}>{chipText}</div>
      <h2
        style={{
          fontSize: "clamp(28px, 3.6vw, 48px)",
          fontWeight: 600,
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          maxWidth: "22ch",
          margin: 0,
        }}
      >
        {renderTitleLines(title)}
      </h2>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          marginTop: 22,
        }}
      >
        {(flowNodes || []).map((n, i) => (
          <React.Fragment key={i}>
            <div style={fnodeStyle(n.variant)}>{n.label}</div>
            {i < flowNodes.length - 1 && (
              <span style={{ color: T.ink3, fontSize: 16 }}>
                {i === flowNodes.length - 2 ? "→" : "+"}
              </span>
            )}
          </React.Fragment>
        ))}
      </div>
      <div
        style={{
          marginTop: 24,
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 16,
        }}
      >
        {(cards || []).map((c, i) => (
          <div
            key={i}
            style={{
              padding: "18px 20px",
              background: c.metric ? T.greenSoft : T.white,
              border: c.metric ? "1px solid transparent" : `1px solid ${T.line}`,
              borderRadius: 14,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: c.metric ? T.green : T.skyDark,
              }}
            >
              {c.tag}
            </div>
            <div
              style={{
                fontSize: c.metric ? 22 : 15,
                fontWeight: 600,
                marginTop: 8,
                letterSpacing: "-0.01em",
                color: c.metric ? T.green : T.ink,
              }}
            >
              {c.ttl}
            </div>
            {c.body && (
              <p
                style={{
                  fontSize: 12.5,
                  color: T.ink2,
                  lineHeight: 1.55,
                  marginTop: 8,
                  margin: "8px 0 0 0",
                }}
              >
                {c.body}
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function SysBox({ box }: { box: ArchSysBox }) {
  return (
    <div style={sysBoxStyle(box.variant)}>
      <span style={sysTagStyle(box.variant)}>{box.tag}</span>
      <div
        style={{
          fontSize: 16,
          fontWeight: 600,
          marginTop: 10,
          letterSpacing: "-0.01em",
          color: box.variant === "dash" ? T.white : T.ink,
        }}
      >
        {box.name}
      </div>
      <div
        style={{
          fontSize: 12,
          color: box.variant === "dash" ? T.white : T.ink2,
          marginTop: 6,
          lineHeight: 1.45,
        }}
      >
        {box.desc}
      </div>
    </div>
  );
}

// ── Generic slide types ────────────────────────────────────────────────────

type Bullet = { title: string; body?: string };

type ContentSlideProps = {
  chipText?: string;
  chipVariant?: ChipVariant;
  title: string;
  lede?: string;
  bullets?: Bullet[];
};

type CompareColumn = {
  heading: string;
  tone?: "neutral" | "blue" | "plum" | "amber";
  bullets: Bullet[];
};

type CompareSlideProps = {
  chipText?: string;
  chipVariant?: ChipVariant;
  title: string;
  left: CompareColumn;
  right: CompareColumn;
};

type QuoteSlideProps = {
  chipText?: string;
  chipVariant?: ChipVariant;
  quote: string;
  attribution?: string;
  caption?: string;
};

type StackItem = {
  num?: string;
  title: string;
  body?: string;
  // MJ-380: pain-first cards render the pain and its automation as two
  // separately-tagged sentences stacked in the same box. `body` stays for
  // back-compat with decks that haven't been migrated.
  pain?: string;
  automation?: string;
};

// Small uppercase tag pill used to label the Pain / Automation lines.
function stackTagStyle(color: string, bg: string): React.CSSProperties {
  return {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color,
    background: bg,
    padding: "2px 8px",
    borderRadius: 5,
    marginBottom: 4,
  };
}

const stackSubBodyStyle: React.CSSProperties = {
  fontSize: 13,
  color: T.ink2,
  lineHeight: 1.5,
};

type StackSlideProps = {
  chipText?: string;
  chipVariant?: ChipVariant;
  title: string;
  lede?: string;
  items: StackItem[];
};

const slideTitleStyle: React.CSSProperties = {
  fontSize: "clamp(28px, 3.6vw, 48px)",
  fontWeight: 600,
  lineHeight: 1.08,
  letterSpacing: "-0.025em",
  maxWidth: "22ch",
  margin: 0,
};

const slideLedeStyle: React.CSSProperties = {
  marginTop: 18,
  fontSize: "clamp(15px, 1.2vw, 19px)",
  color: T.ink2,
  lineHeight: 1.55,
  maxWidth: "62ch",
};

function bulletCardStyle(): React.CSSProperties {
  return {
    padding: "16px 18px",
    background: T.white,
    border: `1px solid ${T.line}`,
    borderRadius: 12,
  };
}

function columnToneStyles(tone: CompareColumn["tone"] = "neutral"): {
  bg: string;
  border: string;
  heading: string;
} {
  switch (tone) {
    case "blue":
      return { bg: T.blueSoft, border: "transparent", heading: T.blue };
    case "plum":
      return { bg: T.plumSoft, border: "transparent", heading: T.plum };
    case "amber":
      return { bg: T.amberSoft, border: "transparent", heading: T.amber };
    default:
      return { bg: T.white, border: T.line, heading: T.skyDark };
  }
}

function ContentSlide({
  chipText,
  chipVariant = "default",
  title,
  lede,
  bullets,
}: ContentSlideProps) {
  return (
    <section data-slide="content" style={slideContainerStyle}>
      {chipText && <div style={chipStyle(chipVariant)}>{chipText}</div>}
      <h2 style={slideTitleStyle}>{renderTitleLines(title)}</h2>
      {lede && <p style={slideLedeStyle}>{lede}</p>}
      {bullets && bullets.length > 0 && (
        <div
          style={{
            marginTop: 28,
            display: "grid",
            gridTemplateColumns:
              bullets.length === 4
                ? "repeat(4, 1fr)"
                : bullets.length >= 3
                  ? "repeat(3, 1fr)"
                  : `repeat(${bullets.length}, 1fr)`,
            gap: 16,
            maxWidth: 1080,
            width: "100%",
          }}
        >
          {bullets.map((b, i) => (
            <div key={i} style={bulletCardStyle()}>
              <div
                style={{
                  fontSize: 14.5,
                  fontWeight: 600,
                  color: T.ink,
                  letterSpacing: "-0.005em",
                }}
              >
                {b.title}
              </div>
              {b.body && (
                <div
                  style={{
                    fontSize: 12.5,
                    color: T.ink2,
                    lineHeight: 1.55,
                    marginTop: 6,
                  }}
                >
                  {b.body}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function CompareSlide({
  chipText,
  chipVariant = "default",
  title,
  left,
  right,
}: CompareSlideProps) {
  const renderColumn = (col: CompareColumn) => {
    const styles = columnToneStyles(col.tone);
    return (
      <div
        style={{
          padding: "20px 22px",
          background: styles.bg,
          border: `1px solid ${styles.border}`,
          borderRadius: 16,
          minHeight: "100%",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: styles.heading,
            marginBottom: 14,
          }}
        >
          {col.heading}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {col.bullets.map((b, i) => (
            <div key={i}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: T.ink,
                  letterSpacing: "-0.005em",
                }}
              >
                {b.title}
              </div>
              {b.body && (
                <div
                  style={{
                    fontSize: 12.5,
                    color: T.ink2,
                    lineHeight: 1.55,
                    marginTop: 4,
                  }}
                >
                  {b.body}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <section data-slide="compare" style={slideContainerStyle}>
      {chipText && <div style={chipStyle(chipVariant)}>{chipText}</div>}
      <h2 style={slideTitleStyle}>{renderTitleLines(title)}</h2>
      <div
        style={{
          marginTop: 28,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 20,
          maxWidth: 1080,
          width: "100%",
        }}
      >
        {renderColumn(left)}
        {renderColumn(right)}
      </div>
    </section>
  );
}

function QuoteSlide({
  chipText,
  chipVariant = "default",
  quote,
  attribution,
  caption,
}: QuoteSlideProps) {
  return (
    <section data-slide="quote" style={slideContainerStyle}>
      {chipText && <div style={chipStyle(chipVariant)}>{chipText}</div>}
      <div
        style={{
          fontSize: "clamp(28px, 3.4vw, 44px)",
          fontWeight: 500,
          lineHeight: 1.22,
          letterSpacing: "-0.018em",
          color: T.ink,
          maxWidth: "28ch",
          paddingLeft: 18,
          borderLeft: `4px solid ${T.skyDark}`,
        }}
      >
        &ldquo;{quote}&rdquo;
      </div>
      {attribution && (
        <div
          style={{
            marginTop: 22,
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: 0.4,
            color: T.skyDark,
          }}
        >
          {attribution}
        </div>
      )}
      {caption && (
        <div
          style={{
            marginTop: 8,
            fontSize: 13,
            color: T.ink3,
            lineHeight: 1.55,
            maxWidth: "60ch",
          }}
        >
          {caption}
        </div>
      )}
    </section>
  );
}

function StackSlide({
  chipText,
  chipVariant = "default",
  title,
  lede,
  items,
}: StackSlideProps) {
  return (
    <section data-slide="stack" style={slideContainerStyle}>
      {chipText && <div style={chipStyle(chipVariant)}>{chipText}</div>}
      <h2 style={slideTitleStyle}>{renderTitleLines(title)}</h2>
      {lede && <p style={slideLedeStyle}>{lede}</p>}
      <div
        style={{
          marginTop: 24,
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 12,
          maxWidth: 1080,
          width: "100%",
        }}
      >
        {items.map((it, i) => (
          <div
            key={i}
            style={{
              padding: "16px 20px",
              background: T.white,
              border: `1px solid ${T.line}`,
              borderRadius: 12,
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: 16,
              alignItems: "start",
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: T.skySoft,
                color: T.accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {it.num ?? String(i + 1).padStart(2, "0")}
            </div>
            <div>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: T.ink,
                  letterSpacing: "-0.005em",
                }}
              >
                {it.title}
              </div>
              {it.pain || it.automation ? (
                <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
                  {it.pain && (
                    <div>
                      <span style={stackTagStyle(T.red, T.redSoft)}>Pain</span>
                      <div style={stackSubBodyStyle}>{it.pain}</div>
                    </div>
                  )}
                  {it.automation && (
                    <div>
                      <span style={stackTagStyle(T.green, T.greenSoft)}>
                        Automation
                      </span>
                      <div style={stackSubBodyStyle}>{it.automation}</div>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    fontSize: 13,
                    color: T.ink2,
                    lineHeight: 1.55,
                    marginTop: 4,
                  }}
                >
                  {it.body}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

type ImageSlideProps = {
  chipText?: string;
  chipVariant?: ChipVariant;
  title: string;
  lede?: string;
  imageUrl: string;
  imageAlt?: string;
  caption?: string;
};

function ImageSlide({
  chipText,
  chipVariant = "default",
  title,
  lede,
  imageUrl,
  imageAlt,
  caption,
}: ImageSlideProps) {
  return (
    <section data-slide="image" style={slideContainerStyle}>
      {chipText && <div style={chipStyle(chipVariant)}>{chipText}</div>}
      <h2 style={slideTitleStyle}>{renderTitleLines(title)}</h2>
      {lede && <p style={slideLedeStyle}>{lede}</p>}
      <div
        style={{
          marginTop: 16,
          width: "100%",
          maxWidth: 1100,
          alignSelf: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
        }}
      >
        <img
          src={imageUrl}
          alt={imageAlt ?? title}
          style={{
            width: "100%",
            height: "auto",
            maxHeight: "62vh",
            objectFit: "contain",
            borderRadius: 16,
            border: `1px solid ${T.line}`,
            boxShadow: "0 8px 32px rgba(28,25,23,0.08)",
            background: T.white,
          }}
        />
        {caption && (
          <p
            style={{
              fontSize: 12,
              color: T.ink3,
              textAlign: "center",
              maxWidth: 720,
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            {caption}
          </p>
        )}
      </div>
    </section>
  );
}

type FlowStep = {
  num?: string;
  title: string;
  body?: string;
  tone?: ChipVariant;
};

type FlowDiagramSlideProps = {
  chipText?: string;
  chipVariant?: ChipVariant;
  title: string;
  lede?: string;
  steps: FlowStep[];
};

function FlowDiagramSlide({
  chipText,
  chipVariant = "default",
  title,
  lede,
  steps,
}: FlowDiagramSlideProps) {
  const toneColor = (tone?: ChipVariant): string => {
    const map: Record<string, string> = {
      default: T.skyDark,
      blue: T.blue,
      green: T.green,
      amber: T.amber,
      plum: T.plum,
      red: T.red,
    };
    return map[tone ?? "default"] ?? T.skyDark;
  };
  return (
    <section data-slide="flow" style={slideContainerStyle}>
      {chipText && <div style={chipStyle(chipVariant)}>{chipText}</div>}
      <h2 style={slideTitleStyle}>{renderTitleLines(title)}</h2>
      {lede && <p style={slideLedeStyle}>{lede}</p>}
      <div
        style={{
          marginTop: 16,
          width: "100%",
          maxWidth: 1180,
          alignSelf: "center",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "stretch",
          gap: 12,
          justifyContent: "center",
        }}
      >
        {steps.map((step, i) => (
          <React.Fragment key={i}>
            <div
              style={{
                flex: "1 1 160px",
                minWidth: 160,
                maxWidth: 220,
                padding: "14px 16px",
                background: T.white,
                border: `1px solid ${T.line}`,
                borderRadius: 12,
                borderTop: `3px solid ${toneColor(step.tone)}`,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {step.num !== undefined && (
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: toneColor(step.tone),
                    letterSpacing: "0.08em",
                  }}
                >
                  {step.num}
                </div>
              )}
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: T.ink,
                  lineHeight: 1.3,
                  letterSpacing: "-0.005em",
                }}
              >
                {step.title}
              </div>
              {step.body && (
                <div
                  style={{
                    fontSize: 12,
                    color: T.ink2,
                    lineHeight: 1.45,
                  }}
                >
                  {step.body}
                </div>
              )}
            </div>
            {i < steps.length - 1 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: T.ink3,
                  fontSize: 18,
                  flex: "0 0 16px",
                  fontWeight: 300,
                }}
              >
                →
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}

// ── WorkflowSlide ──────────────────────────────────────────────────────────
// Ranked workflows as a grid of cards (default two columns). Each card: a
// numbered name header, then a "What it does" block stacked above a colored
// "Why it matters" block. Used for the discovery-analysis recommendation slides.
// Optional per-item `impact` + `cost` render as two pills in the card's
// top-right: an amber "High impact" and a slate "Low cost". Values are
// "High" | "Medium" | "Low". Either field absent => that pill is skipped.

type WorkflowItem = {
  num?: string;
  name: string;
  what: string;
  why: string;
  impact?: string;
  cost?: string;
};

type WorkflowSlideProps = {
  chipText?: string;
  chipVariant?: ChipVariant;
  title: string;
  lede?: string;
  items: WorkflowItem[];
  whatLabel?: string;
  whyLabel?: string;
  whyTone?: ChipVariant;
  columns?: number;
};

function miniTagStyle(variant: ChipVariant): React.CSSProperties {
  const map: Record<string, [string, string]> = {
    default: [T.skySoft, T.accent],
    blue: [T.blueSoft, T.blue],
    green: [T.greenSoft, T.green],
    amber: [T.amberSoft, T.amber],
    plum: [T.plumSoft, T.plum],
    red: [T.redSoft, T.red],
  };
  const [bg, color] = map[variant] ?? map.default;
  return {
    display: "inline-block",
    fontSize: 9.5,
    fontWeight: 700,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    padding: "2px 8px",
    borderRadius: 6,
    background: bg,
    color,
    marginBottom: 6,
    whiteSpace: "nowrap",
  };
}

const pillBase: React.CSSProperties = {
  flexShrink: 0,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.2,
  padding: "3px 9px",
  borderRadius: 999,
  whiteSpace: "nowrap",
};

// Two rating pills, top-right of each card: impact in our amber/orange,
// cost in a calm slate-gray. The level lives in the word ("High impact").
const impactPillStyle: React.CSSProperties = {
  ...pillBase,
  background: T.amberSoft,
  color: T.amber,
};

const costPillStyle: React.CSSProperties = {
  ...pillBase,
  background: "#E9ECF0",
  color: "#515A66",
};

function WorkflowSlide({
  chipText,
  chipVariant = "default",
  title,
  lede,
  items,
  whatLabel = "What it does",
  whyLabel = "Why it matters",
  whyTone = "green",
  columns = 2,
}: WorkflowSlideProps) {
  return (
    <section data-slide="workflow" style={slideContainerStyle}>
      {chipText && <div style={chipStyle(chipVariant)}>{chipText}</div>}
      <h2 style={slideTitleStyle}>{renderTitleLines(title)}</h2>
      {lede && <p style={slideLedeStyle}>{lede}</p>}
      <div
        style={{
          marginTop: 18,
          display: "grid",
          gridTemplateColumns: columns === 1 ? "1fr" : `repeat(${columns}, 1fr)`,
          gap: 14,
          maxWidth: 1120,
          width: "100%",
        }}
      >
        {(items || []).map((it, i) => (
          <div
            key={i}
            style={{
              padding: "16px 18px",
              background: T.white,
              border: `1px solid ${T.line}`,
              borderRadius: 14,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  background: T.skySoft,
                  color: T.accent,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {it.num ?? String(i + 1).padStart(2, "0")}
              </div>
              <div
                style={{
                  fontSize: 15.5,
                  fontWeight: 600,
                  color: T.ink,
                  letterSpacing: "-0.01em",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {it.name}
              </div>
              {/* impact + cost rating pills, top-right; each omitted when absent */}
              {(it.impact || it.cost) && (
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {it.impact && (
                    <div style={impactPillStyle}>{it.impact} impact</div>
                  )}
                  {it.cost && <div style={costPillStyle}>{it.cost} cost</div>}
                </div>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <div style={miniTagStyle("default")}>{whatLabel}</div>
                <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5 }}>
                  {it.what}
                </div>
              </div>
              <div>
                <div style={miniTagStyle(whyTone)}>{whyLabel}</div>
                <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5 }}>
                  {it.why}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── ExecSummarySlide ───────────────────────────────────────────────────────
// An editorial executive-summary layout — NOT cards/boxes. Two content modes,
// both light + magazine-feel, on the deck's bone-bg / ink-text system:
//
//   * `sections: [{heading, body}]` — the refined mode. Each section is a
//     subtitled block: a short accent-colored, letter-spaced heading marked by
//     a slim vertical accent bar to its left, then prose body beneath. Sections
//     are generously spaced and divided by a hairline rule — editorial, never
//     bordered cards. Use this when the summary has distinct beats.
//   * `paragraphs: string[]` — the simpler mode (used by the final slide). A
//     column of prose at a comfortable ~62ch measure, generous line-height,
//     refined spacing. Still no borders.
//
// If both are supplied, `sections` wins. Degrades gracefully: missing chip /
// title / sections / paragraphs all render nothing rather than crashing.

type ExecSummarySection = {
  heading: string;
  body: string;
};

type ExecSummarySlideProps = {
  chipText?: string;
  chipVariant?: ChipVariant;
  title: string;
  sections?: ExecSummarySection[];
  paragraphs?: string[];
};

// Accent color for the section heading + bar, keyed to the chip variant so the
// slide reads as one tonal family.
function execAccent(variant: ChipVariant): string {
  const map: Record<string, string> = {
    default: T.skyDark,
    blue: T.blue,
    green: T.green,
    amber: T.amber,
    plum: T.plum,
    red: T.red,
  };
  return map[variant] ?? T.skyDark;
}

function ExecSummarySlide({
  chipText,
  chipVariant = "default",
  title,
  sections,
  paragraphs,
}: ExecSummarySlideProps) {
  const accent = execAccent(chipVariant);
  const hasSections = sections && sections.length > 0;

  return (
    <section data-slide="execsummary" style={slideContainerStyle}>
      {chipText && <div style={chipStyle(chipVariant)}>{chipText}</div>}
      <h2 style={slideTitleStyle}>{renderTitleLines(title)}</h2>

      {hasSections ? (
        <div
          style={{
            marginTop: 36,
            display: "flex",
            flexDirection: "column",
            maxWidth: "66ch",
            width: "100%",
          }}
        >
          {sections!.map((s, i) => (
            <div
              key={i}
              style={{
                paddingTop: i === 0 ? 0 : 26,
                marginTop: i === 0 ? 0 : 26,
                borderTop: i === 0 ? "none" : `1px solid ${T.line}`,
              }}
            >
              {/* accent-bar + heading */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 10,
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: 3,
                    height: 15,
                    borderRadius: 2,
                    background: accent,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 12.5,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: accent,
                  }}
                >
                  {s.heading}
                </span>
              </div>
              <p
                style={{
                  margin: 0,
                  paddingLeft: 15,
                  fontSize: "clamp(15px, 1.2vw, 19px)",
                  color: T.ink2,
                  lineHeight: 1.65,
                  letterSpacing: "-0.005em",
                }}
              >
                {s.body}
              </p>
            </div>
          ))}
        </div>
      ) : (
        paragraphs &&
        paragraphs.length > 0 && (
          <div
            style={{
              marginTop: 30,
              display: "flex",
              flexDirection: "column",
              gap: 20,
              maxWidth: "62ch",
              width: "100%",
            }}
          >
            {paragraphs.map((p, i) => (
              <p
                key={i}
                style={{
                  margin: 0,
                  fontSize: "clamp(15px, 1.2vw, 19px)",
                  color: T.ink2,
                  lineHeight: 1.7,
                  letterSpacing: "-0.005em",
                }}
              >
                {p}
              </p>
            ))}
          </div>
        )
      )}
    </section>
  );
}

// ── CostSimSlide ───────────────────────────────────────────────────────────
// A model-comparison table for a worked example. Structured subtitle: a single
// opening `lede` line, then `points[]` rendered as a small numbered list of the
// assumptions. Beneath it, a worked-row calculation strip (`calc`): factor ×
// factor × factor = result, so the client can SEE how a monthly runs number is
// built. Then the table: workflow name, runs/month, and two model cost columns
// (Haiku / Sonnet). Every figure in the table is rendered at ONE consistent
// size — usage is a peer column, not a hero. The Sonnet (modelB) column is the
// emphasized option, its figures in a tight green pill; Haiku (modelA) is plain
// ink. The total row is separated by a top border and set bold (same size).
// `note` is a short highlighted line under the table; `footnote` is small muted
// text at the very bottom.
//
// Degrades gracefully: missing points / calc / columns / rows / cells / total /
// note / footnote all render nothing rather than crashing.

type CostRow = {
  name: string;
  runs?: string;
  runsBusy?: string;
  costA?: string;
  costB?: string;
};

type CostColumns = {
  runs?: string;
  modelA?: string;
  modelB?: string;
};

type CostTotal = {
  name?: string;
  runs?: string;
  costA?: string;
  costB?: string;
};

// One factor (or the result) in the worked-row calculation strip.
type CalcFactor = { value: string; label: string };
type CostCalc = {
  factors: CalcFactor[];
  result: CalcFactor;
  note?: string;
};

type CostSimSlideProps = {
  chipText?: string;
  chipVariant?: ChipVariant;
  title: string;
  lede?: string;
  // Structured subtitle: `lede` is the single opening line; `points` are the
  // assumption bullets rendered as a small numbered list beneath it.
  points?: string[];
  // The worked-row calculation, rendered as a visual equation strip under the
  // subtitle (factor × factor × factor = result).
  calc?: CostCalc;
  columns?: CostColumns;
  rows?: CostRow[];
  total?: CostTotal;
  note?: string;
  footnote?: string;
};

// grid: workflow-name | runs/month | modelA | modelB. All four cells now share
// one type size — usage is no longer a visual "hero", it's a peer column.
const COST_GRID = "minmax(180px, 1.5fr) minmax(120px, 1fr) 0.9fr 0.9fr";

// One operator glyph (×, =) between calculation cells.
function CalcOp({ symbol }: { symbol: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        color: T.ink3,
        fontSize: 20,
        fontWeight: 400,
        padding: "0 2px",
      }}
    >
      {symbol}
    </div>
  );
}

// One value+label cell in the calculation strip. `highlight` paints the result
// in the green accent so the eye lands on the answer.
function CalcCell({
  value,
  label,
  highlight,
}: {
  value: string;
  label: string;
  highlight?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "8px 16px",
        borderRadius: 12,
        background: highlight ? T.greenSoft : T.bg,
        minWidth: 92,
      }}
    >
      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
          color: highlight ? T.green : T.ink,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 11,
          color: highlight ? T.green : T.ink3,
          fontWeight: highlight ? 600 : 500,
          marginTop: 3,
          textAlign: "center",
          lineHeight: 1.25,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function CostSimSlide({
  chipText,
  chipVariant = "default",
  title,
  lede,
  points,
  calc,
  columns,
  rows,
  total,
  note,
  footnote,
}: CostSimSlideProps) {
  const col = columns ?? {};
  const runsHeader = col.runs || "Runs / month";
  const modelAHeader = col.modelA || "Model A";
  const modelBHeader = col.modelB || "Model B";

  // Plain cell container for BOTH model columns — no background. The
  // highlighted (Sonnet / modelB) value gets a tight pill via pillStyle; the
  // Haiku (modelA) value is plain ink. Left-aligned, vertically centered.
  const cellStyle: React.CSSProperties = {
    padding: "10px 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  };
  // Tight green pill that hugs the highlighted (Sonnet / modelB) figure —
  // inline-block sized to its content, not a full-width block.
  const pillStyle: React.CSSProperties = {
    display: "inline-block",
    padding: "4px 12px",
    borderRadius: 999,
    background: T.greenSoft,
    color: T.green,
    fontWeight: 700,
    whiteSpace: "nowrap",
    width: "fit-content",
  };

  return (
    <section data-slide="costsim" style={slideContainerStyle}>
      {chipText && <div style={chipStyle(chipVariant)}>{chipText}</div>}
      <h2 style={slideTitleStyle}>{renderTitleLines(title)}</h2>
      {lede && <p style={{ ...slideLedeStyle, marginBottom: 0 }}>{lede}</p>}

      {/* Structured subtitle — assumption points as a small numbered list */}
      {points && points.length > 0 && (
        <div
          style={{
            marginTop: 14,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            maxWidth: "72ch",
          }}
        >
          {points.map((p, i) => (
            <div
              key={i}
              style={{ display: "flex", alignItems: "flex-start", gap: 10 }}
            >
              <span
                style={{
                  flexShrink: 0,
                  width: 20,
                  height: 20,
                  borderRadius: 999,
                  background: T.bg2,
                  color: T.accent,
                  fontSize: 11,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: 2,
                }}
              >
                {i + 1}
              </span>
              <span style={{ fontSize: 14.5, color: T.ink2, lineHeight: 1.45 }}>
                {p}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Worked-row calculation strip */}
      {calc && calc.factors && calc.factors.length > 0 && (
        <div
          style={{
            marginTop: 18,
            width: "100%",
            maxWidth: 1120,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "stretch",
              flexWrap: "wrap",
              gap: 8,
              background: T.white,
              border: `1px solid ${T.line}`,
              borderRadius: 14,
              padding: "14px 18px",
              boxShadow: "0 4px 20px rgba(28,25,23,0.04)",
            }}
          >
            {calc.factors.map((f, i) => (
              <React.Fragment key={i}>
                {i > 0 && <CalcOp symbol="×" />}
                <CalcCell value={f.value} label={f.label} />
              </React.Fragment>
            ))}
            <CalcOp symbol="=" />
            <CalcCell
              value={calc.result.value}
              label={calc.result.label}
              highlight
            />
          </div>
          {calc.note && (
            <p
              style={{
                margin: 0,
                paddingLeft: 2,
                fontSize: 12,
                color: T.ink3,
                lineHeight: 1.4,
              }}
            >
              {calc.note}
            </p>
          )}
        </div>
      )}

      <div
        style={{
          marginTop: 18,
          width: "100%",
          maxWidth: 1120,
          background: T.white,
          border: `1px solid ${T.line}`,
          borderRadius: 16,
          padding: "8px 18px 14px",
          boxShadow: "0 4px 20px rgba(28,25,23,0.04)",
        }}
      >
        {/* Header row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: COST_GRID,
            gap: 10,
            alignItems: "end",
            padding: "9px 0",
            borderBottom: `1px solid ${T.line}`,
          }}
        >
          <div />
          {/* Runs / month header */}
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 800,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: T.ink,
              textAlign: "left",
              paddingLeft: 4,
            }}
          >
            {runsHeader}
          </div>
          {/* Haiku (modelA) — plain / muted */}
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: T.ink3,
              textAlign: "left",
              paddingLeft: 14,
            }}
          >
            {modelAHeader}
          </div>
          {/* Sonnet (modelB) — the emphasized option, green header */}
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: T.green,
              textAlign: "left",
              paddingLeft: 14,
            }}
          >
            {modelBHeader}
          </div>
        </div>

        {/* Data rows */}
        {(rows || []).map((row, ri) => (
          <div
            key={ri}
            style={{
              display: "grid",
              gridTemplateColumns: COST_GRID,
              gap: 10,
              alignItems: "center",
              padding: "7px 0",
              borderBottom: `1px solid ${T.bg2}`,
            }}
          >
            {/* Workflow name */}
            <div
              style={{
                fontSize: 14.5,
                fontWeight: 600,
                color: T.ink,
                letterSpacing: "-0.005em",
                paddingRight: 8,
              }}
            >
              {row.name}
            </div>
            {/* Runs / month — now a peer column, same size as the costs */}
            <div style={{ paddingLeft: 4 }}>
              {row.runs && (
                <div
                  style={{
                    fontSize: 14.5,
                    fontWeight: 700,
                    color: T.ink,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.2,
                  }}
                >
                  {row.runs}
                </div>
              )}
              {row.runsBusy && (
                <div
                  style={{
                    fontSize: 11.5,
                    color: T.ink3,
                    marginTop: 3,
                    lineHeight: 1.2,
                  }}
                >
                  {row.runsBusy}
                </div>
              )}
            </div>
            {/* Haiku cost (modelA) — plain ink */}
            <div style={cellStyle}>
              {row.costA && (
                <span
                  style={{
                    fontSize: 14.5,
                    fontWeight: 600,
                    color: T.ink,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {row.costA}
                </span>
              )}
            </div>
            {/* Sonnet cost (modelB) — highlighted green pill */}
            <div style={cellStyle}>
              {row.costB && (
                <span
                  style={{
                    ...pillStyle,
                    fontSize: 14.5,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {row.costB}
                </span>
              )}
            </div>
          </div>
        ))}

        {/* Total row — separated by a stronger top border, bold (same size) */}
        {total && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: COST_GRID,
              gap: 10,
              alignItems: "center",
              padding: "10px 0 4px",
              marginTop: 4,
              borderTop: `2px solid ${T.line}`,
            }}
          >
            <div
              style={{
                fontSize: 14.5,
                fontWeight: 800,
                color: T.ink,
                letterSpacing: "-0.005em",
                paddingRight: 8,
              }}
            >
              {total.name}
            </div>
            <div style={{ paddingLeft: 4 }}>
              {total.runs && (
                <div
                  style={{
                    fontSize: 14.5,
                    fontWeight: 800,
                    color: T.ink,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.2,
                  }}
                >
                  {total.runs}
                </div>
              )}
            </div>
            <div style={{ ...cellStyle, padding: "4px 14px" }}>
              {total.costA && (
                <span
                  style={{
                    fontSize: 14.5,
                    fontWeight: 800,
                    color: T.ink,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {total.costA}
                </span>
              )}
            </div>
            <div style={{ ...cellStyle, padding: "4px 14px" }}>
              {total.costB && (
                <span
                  style={{
                    ...pillStyle,
                    fontSize: 14.5,
                    fontWeight: 800,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {total.costB}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Highlighted note line under the table */}
      {note && (
        <div
          style={{
            marginTop: 16,
            maxWidth: 1120,
            width: "100%",
            background: T.skySoft,
            borderRadius: 12,
            padding: "12px 16px",
            fontSize: 13.5,
            fontWeight: 500,
            color: T.accent,
            lineHeight: 1.5,
          }}
        >
          {note}
        </div>
      )}

      {footnote && (
        <p
          style={{
            marginTop: 12,
            fontSize: 11.5,
            color: T.ink3,
            lineHeight: 1.5,
            maxWidth: "92ch",
          }}
        >
          {footnote}
        </p>
      )}
    </section>
  );
}

// ── DiagramSlide ──────────────────────────────────────────────────────────
// A slide whose content is a boxes-and-arrows SVG. Same data shape the board
// used for its hand-rolled diagrams, so those move into the deck unchanged
// (Erez, 2026-07-25: the diagrams should just be part of the architecture deck).
//
// Every element carries a `step`. The clickable deck renders all steps at once;
// the Remotion film reveals step N as the narration reaches it, so the same
// data drives a static picture and a self-drawing one.

type DiaTone = "green" | "amber" | "blue" | "plum" | "petrol" | "red" | "slate";
type DiaBox = { x: number; y: number; w: number; h: number; label: string; sub?: string; tone?: DiaTone; step?: number };
type DiaArrow = { x1: number; y1: number; x2: number; y2: number; dashed?: boolean; tone?: DiaTone; step?: number; label?: string };

// [fill, stroke] — soft body, saturated edge, so a dense diagram stays readable.
const DIA_TONE: Record<DiaTone, [string, string]> = {
  green: [T.greenSoft, T.green],
  amber: [T.amberSoft, T.amber],
  blue: [T.blueSoft, T.blue],
  plum: [T.plumSoft, T.plum],
  petrol: ["#E0EEF3", "#2C7E9E"],
  red: [T.redSoft, T.red],
  slate: ["#FFFFFF", T.line],
};

export function DiagramSvg({ boxes, arrows, vw, vh, visibleStep = Infinity, id = "d" }: {
  boxes: DiaBox[]; arrows: DiaArrow[]; vw: number; vh: number; visibleStep?: number; id?: string;
}) {
  return (
    <svg viewBox={`0 0 ${vw} ${vh}`} style={{ width: "100%", height: "auto", display: "block", fontFamily: FONT }}>
      <defs>
        <marker id={`${id}-ah`} markerWidth="9" markerHeight="9" refX="6.5" refY="3" orient="auto">
          <path d="M0,0 L6.5,3 L0,6 Z" fill="#8FA3B8" />
        </marker>
      </defs>
      {arrows.map((a, i) => {
        if ((a.step ?? 0) > visibleStep) return null;
        const stroke = a.tone ? DIA_TONE[a.tone][1] : "#8FA3B8";
        const mx = (a.x1 + a.x2) / 2, my = (a.y1 + a.y2) / 2;
        return (
          <g key={`a${i}`}>
            <path d={`M${a.x1},${a.y1} L${a.x2},${a.y2}`} stroke={stroke} strokeWidth={2} fill="none"
              markerEnd={`url(#${id}-ah)`} strokeDasharray={a.dashed ? "6 5" : undefined} />
            {a.label && (
              <text x={mx} y={my - 7} textAnchor="middle" fontSize={12} fontWeight={600} fill={stroke}
                style={{ paintOrder: "stroke", stroke: T.bg, strokeWidth: 5 }}>{a.label}</text>
            )}
          </g>
        );
      })}
      {boxes.map((b, i) => {
        if ((b.step ?? 0) > visibleStep) return null;
        const [fill, stroke] = DIA_TONE[b.tone ?? "slate"];
        return (
          <g key={`b${i}`}>
            <rect x={b.x} y={b.y} width={b.w} height={b.h} rx={12} fill={fill} stroke={stroke} strokeWidth={2} />
            <text x={b.x + b.w / 2} y={b.sub ? b.y + b.h / 2 - 8 : b.y + b.h / 2 + 1}
              textAnchor="middle" dominantBaseline="middle" fontSize={15} fontWeight={700} fill={T.ink}>{b.label}</text>
            {b.sub && (
              <text x={b.x + b.w / 2} y={b.y + b.h / 2 + 12} textAnchor="middle" dominantBaseline="middle"
                fontSize={12} fontWeight={500} fill={T.ink2}>{b.sub}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

type DiagramSlideProps = {
  chipText?: string; chipVariant?: ChipVariant; title: string; lede?: string;
  boxes: DiaBox[]; arrows: DiaArrow[]; vw: number; vh: number; note?: string;
};

function DiagramSlide({ chipText, chipVariant = "default", title, lede, boxes, arrows, vw, vh, note }: DiagramSlideProps) {
  return (
    <section data-slide="diagram" style={slideContainerStyle}>
      {chipText && <div style={chipStyle(chipVariant)}>{chipText}</div>}
      <h2 style={slideTitleStyle}>{renderTitleLines(title)}</h2>
      {lede && <p style={{ ...slideLedeStyle, marginTop: 10 }}>{lede}</p>}
      <div style={{ width: "100%", maxWidth: 1180, marginTop: 24 }}>
        <DiagramSvg boxes={boxes} arrows={arrows} vw={vw} vh={vh} />
      </div>
      {note && <p style={{ fontSize: 13, color: T.ink3, marginTop: 16, maxWidth: 1000, lineHeight: 1.55 }}>{note}</p>}
    </section>
  );
}

// ── ChatSlide ─────────────────────────────────────────────────────────────
// A slide whose content is a CONVERSATION. Built for the Falcon use-case deck
// (Erez, 2026-07-25: "I want to first see all the Telegram interactions") where
// describing a feature is strictly worse than showing the exchange that
// delivers it. The analytical frame sits left, the chat right.
//
// Message kinds are deliberately rich, because the point is that the agent
// speaks in more than text: voice notes, photo grids, documents, structured
// records, ranked options, an offer log, a generated deck, an email, a
// schedule, a timeline, inline keyboards.
//
// The same message data drives the Remotion film, which reads this row and
// animates each bubble onto the beat it is narrated (skill://remotion §06b).

const TGC = {
  bg: "#0E1621", head: "#17212B", in: "#182533", out: "#2B5278",
  text: "#E7EDF3", dim: "#7E8B99", link: "#64B5EF", accent: "#5288C1",
  panel: "#1D2A38", hair: "#26374A",
};
const CHAT_MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
const hasHebrew = (s: unknown) => /[֐-׿]/.test(String(s ?? ""));
const waveBars = (n: number) =>
  Array.from({ length: n }, (_, i) => 4 + ((i * 7 + (i % 5) * 11 + (i % 3) * 6) % 15));

function ChatRow({ out, children }: { out?: boolean; children: React.ReactNode }) {
  return <div style={{ display: "flex", justifyContent: out ? "flex-end" : "flex-start" }}>{children}</div>;
}

function ChatBubble({ out, children, pad }: { out?: boolean; children: React.ReactNode; pad?: string }) {
  return (
    <div style={{ maxWidth: "88%", fontSize: 13, lineHeight: 1.55, padding: pad ?? "8px 12px",
      borderRadius: 12, color: TGC.text, whiteSpace: "pre-line", boxSizing: "border-box",
      background: out ? TGC.out : TGC.in }}>{children}</div>
  );
}

const Bidi: React.FC<{ t: string }> = ({ t }) => (
  <span style={{ display: "block", direction: hasHebrew(t) ? "rtl" : "ltr", textAlign: hasHebrew(t) ? "right" : "left" }}>{t}</span>
);

function ChatMessage({ m }: { m: Record<string, any> }) {
  switch (m.k) {
    case "day":
      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: TGC.dim, background: "rgba(255,255,255,0.06)", borderRadius: 999, padding: "3px 12px" }}>{m.t}</span>
        </div>
      );
    case "text":
      return (
        <ChatRow out={m.out}>
          <ChatBubble out={m.out}>
            {m.quote && <div style={{ borderInlineStart: `3px solid ${TGC.link}`, paddingInlineStart: 9, marginBottom: 6, fontSize: 12, color: "#B8C6D4", direction: "rtl", textAlign: "right" }}>{m.quote}</div>}
            <Bidi t={m.t} />
          </ChatBubble>
        </ChatRow>
      );
    case "voice":
      return (
        <ChatRow out={m.out}>
          <ChatBubble out={m.out}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 250 }}>
              <span style={{ width: 30, height: 30, borderRadius: 999, background: m.out ? "#6AB3F3" : TGC.accent, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, flexShrink: 0 }}>▶</span>
              <span style={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                {waveBars(30).map((h, j) => <span key={j} style={{ width: 2, height: h, borderRadius: 2, background: m.out ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.45)" }} />)}
              </span>
              <span style={{ fontSize: 10.5, color: m.out ? "#C9DDF0" : TGC.dim, flexShrink: 0 }}>{m.sec}</span>
            </div>
            {m.transcript && (
              <div style={{ marginTop: 7, paddingTop: 7, borderTop: "1px solid rgba(255,255,255,0.12)", fontSize: 12.5, lineHeight: 1.55, color: "#C9D6E2", direction: "rtl", textAlign: "right" }}>
                <span style={{ fontFamily: CHAT_MONO, fontSize: 9, letterSpacing: "0.08em", color: TGC.dim, display: "block", marginBottom: 3, direction: "ltr", textAlign: "left" }}>TRANSCRIPT</span>
                {m.transcript}
              </div>
            )}
          </ChatBubble>
        </ChatRow>
      );
    case "photos":
      return (
        <ChatRow out={m.out}>
          <ChatBubble out={m.out} pad="4px">
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(m.imgs.length, 3)}, 1fr)`, gap: 3, width: m.imgs.length === 1 ? 190 : 320 }}>
              {m.imgs.map((src: string, j: number) => (
                <img key={j} src={src} alt="" loading="eager" decoding="async" style={{ width: "100%", height: m.imgs.length === 1 ? 130 : 88, objectFit: "cover", borderRadius: 8, display: "block" }} />
              ))}
            </div>
            {m.cap && <div style={{ padding: "6px 6px 2px", fontSize: 12.5, direction: "rtl", textAlign: "right" }}>{m.cap}</div>}
          </ChatBubble>
        </ChatRow>
      );
    case "doc":
      return (
        <ChatRow out={m.out}>
          <ChatBubble out={m.out}>
            <div style={{ display: "flex", alignItems: "center", gap: 11, minWidth: 230 }}>
              <span style={{ width: 36, height: 36, borderRadius: 9, background: m.tone ?? "#C0492F", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 10, fontWeight: 800, fontFamily: CHAT_MONO, flexShrink: 0 }}>PDF</span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 12.5, fontWeight: 600 }}>{m.name}</span>
                <span style={{ display: "block", fontSize: 10.5, color: m.out ? "#9FC0E0" : TGC.dim }}>{m.size}</span>
              </span>
            </div>
            {m.cap && <div style={{ marginTop: 7, fontSize: 12.5 }}><Bidi t={m.cap} /></div>}
          </ChatBubble>
        </ChatRow>
      );
    case "deck":
      return (
        <ChatRow>
          <ChatBubble pad="8px">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <span style={{ width: 34, height: 34, borderRadius: 8, background: "#2E6B52", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 9.5, fontWeight: 800, fontFamily: CHAT_MONO, flexShrink: 0 }}>PDF</span>
              <span>
                <span style={{ display: "block", fontSize: 12.5, fontWeight: 600 }}>{m.name}</span>
                <span style={{ display: "block", fontSize: 10.5, color: TGC.dim }}>{m.size}</span>
              </span>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {m.pages.map((p: any, j: number) => (
                <div key={j} style={{ width: 104, background: "#F7F5F1", borderRadius: 7, overflow: "hidden", flexShrink: 0 }}>
                  <img src={p.img} alt="" decoding="async" style={{ width: "100%", height: 50, objectFit: "cover", display: "block" }} />
                  <div style={{ padding: "5px 6px 6px", direction: "rtl", textAlign: "right" }}>
                    <div style={{ fontSize: 9.5, fontWeight: 700, color: "#1B211E", lineHeight: 1.25 }}>{p.t}</div>
                    <div style={{ fontSize: 8.5, color: "#8C948E", marginTop: 2 }}>{p.s}</div>
                  </div>
                </div>
              ))}
            </div>
          </ChatBubble>
        </ChatRow>
      );
    case "record":
      return (
        <ChatRow>
          <ChatBubble pad="0">
            <div style={{ width: 380, background: TGC.panel, borderRadius: 12, overflow: "hidden", border: `1px solid ${TGC.hair}` }}>
              <div style={{ padding: "8px 13px", background: "rgba(63,143,111,0.16)", borderBottom: `1px solid ${TGC.hair}`, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#8FD8B8", direction: "rtl", textAlign: "right", flex: 1 }}>{m.title}</span>
                {m.badge && <span style={{ fontSize: 9, fontFamily: CHAT_MONO, fontWeight: 700, color: "#0E1621", background: "#8FD8B8", borderRadius: 999, padding: "2px 7px" }}>{m.badge}</span>}
              </div>
              <div style={{ padding: "8px 13px", display: "grid", gridTemplateColumns: "auto 1fr", gap: "5px 12px", direction: "rtl" }}>
                {m.rows.map((r: [string, string], j: number) => (
                  <React.Fragment key={j}>
                    <span style={{ fontSize: 11, color: TGC.dim, whiteSpace: "nowrap", textAlign: "right" }}>{r[0]}</span>
                    <span style={{ fontSize: 12, color: TGC.text, textAlign: "right" }}>{r[1]}</span>
                  </React.Fragment>
                ))}
              </div>
              {m.note && <div style={{ padding: "7px 13px 9px", fontSize: 11.5, color: "#E0B978", direction: "rtl", textAlign: "right", borderTop: `1px solid ${TGC.hair}` }}>{m.note}</div>}
            </div>
          </ChatBubble>
        </ChatRow>
      );
    case "options":
      return (
        <ChatRow>
          <ChatBubble pad="0">
            <div style={{ width: 430, background: TGC.panel, borderRadius: 12, overflow: "hidden", border: `1px solid ${TGC.hair}` }}>
              <div style={{ padding: "8px 13px", borderBottom: `1px solid ${TGC.hair}`, fontSize: 12, fontWeight: 700, color: TGC.link, direction: "rtl", textAlign: "right" }}>{m.head}</div>
              {m.items.map((it: any, j: number) => (
                <div key={j} style={{ display: "flex", gap: 10, padding: "8px 13px", borderBottom: j < m.items.length - 1 ? `1px solid ${TGC.hair}` : "none", direction: "rtl" }}>
                  {it.img && <img src={it.img} alt="" decoding="async" style={{ width: 52, height: 40, objectFit: "cover", borderRadius: 6, flexShrink: 0 }} />}
                  <div style={{ minWidth: 0, flex: 1, textAlign: "right" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: TGC.text }}>{it.t}</div>
                    <div style={{ fontSize: 10.5, color: TGC.dim, marginTop: 1 }}>{it.meta}</div>
                    <div style={{ fontSize: 11, color: "#A8C4DC", marginTop: 2 }}>{it.why}</div>
                    {it.flag && <div style={{ fontSize: 10, color: "#E0B978", marginTop: 2 }}>⚠ {it.flag}</div>}
                  </div>
                </div>
              ))}
            </div>
          </ChatBubble>
        </ChatRow>
      );
    case "pips": {
      const tone: Record<string, [string, string]> = {
        bit: ["#8FD8B8", "נגס"], out: ["#E08A78", "לא רלוונטי"], open: ["#9FC0E0", "הוצע"], cold: ["#B9A88F", "התקרר"],
      };
      return (
        <ChatRow>
          <ChatBubble pad="0">
            <div style={{ width: 420, background: TGC.panel, borderRadius: 12, overflow: "hidden", border: `1px solid ${TGC.hair}` }}>
              <div style={{ padding: "8px 13px", borderBottom: `1px solid ${TGC.hair}`, fontSize: 12, fontWeight: 700, color: TGC.link, direction: "rtl", textAlign: "right" }}>{m.head}</div>
              {m.rows.map((r: any, j: number) => (
                <div key={j} style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 13px", borderBottom: j < m.rows.length - 1 ? `1px solid ${TGC.hair}` : "none", direction: "rtl" }}>
                  <span style={{ fontSize: 9.5, fontWeight: 700, fontFamily: CHAT_MONO, color: "#0E1621", background: (tone[r.state] ?? tone.open)[0], borderRadius: 999, padding: "2px 8px", flexShrink: 0, minWidth: 62, textAlign: "center" }}>{(tone[r.state] ?? tone.open)[1]}</span>
                  <span style={{ fontSize: 12, color: TGC.text, flexShrink: 0, minWidth: 110, textAlign: "right" }}>{r.t}</span>
                  <span style={{ fontSize: 10.5, color: TGC.dim, textAlign: "right", flex: 1 }}>{r.note}</span>
                </div>
              ))}
            </div>
          </ChatBubble>
        </ChatRow>
      );
    }
    case "email":
      return (
        <ChatRow>
          <ChatBubble pad="0">
            <div style={{ width: 430, background: "#F7F5F1", borderRadius: 12, overflow: "hidden", color: "#1B211E" }}>
              <div style={{ padding: "8px 13px", borderBottom: "1px solid #E3DED4", display: "grid", gridTemplateColumns: "auto 1fr", gap: "3px 10px", direction: "rtl" }}>
                <span style={{ fontSize: 10, color: "#8C948E", textAlign: "right" }}>אל</span>
                <span style={{ fontSize: 11, color: "#4A534D", textAlign: "right" }}>{m.to}</span>
                <span style={{ fontSize: 10, color: "#8C948E", textAlign: "right" }}>נושא</span>
                <span style={{ fontSize: 12, fontWeight: 700, textAlign: "right" }}>{m.subj}</span>
              </div>
              <div style={{ padding: "10px 13px", fontSize: 11.5, lineHeight: 1.6, color: "#4A534D", direction: "rtl", textAlign: "right", whiteSpace: "pre-line" }}>{m.body}</div>
              <div style={{ padding: "8px 13px 10px", borderTop: "1px solid #E3DED4", display: "flex", gap: 6, flexWrap: "wrap", direction: "rtl" }}>
                {m.attach.map((a: string, j: number) => (
                  <span key={j} style={{ fontSize: 10, color: "#4A534D", background: "#F2EDE4", border: "1px solid #E3DED4", borderRadius: 999, padding: "3px 10px" }}>📎 {a}</span>
                ))}
              </div>
            </div>
          </ChatBubble>
        </ChatRow>
      );
    case "sched":
      return (
        <ChatRow>
          <ChatBubble pad="0">
            <div style={{ width: 400, background: TGC.panel, borderRadius: 12, overflow: "hidden", border: `1px solid ${TGC.hair}` }}>
              <div style={{ padding: "8px 13px", borderBottom: `1px solid ${TGC.hair}`, fontSize: 12, fontWeight: 700, color: TGC.link, direction: "rtl", textAlign: "right" }}>{m.head}</div>
              {m.rows.map((r: any, j: number) => (
                <div key={j} style={{ display: "flex", alignItems: "center", gap: 11, padding: "7px 13px", borderBottom: j < m.rows.length - 1 ? `1px solid ${TGC.hair}` : "none", direction: "rtl" }}>
                  <span style={{ fontFamily: CHAT_MONO, fontSize: 11, color: "#8FD8B8", flexShrink: 0, minWidth: 46 }}>{r.time}</span>
                  <span style={{ fontSize: 12, color: TGC.text, flex: 1, textAlign: "right" }}>{r.what}</span>
                  <span style={{ fontSize: 10, color: r.ok ? "#8FD8B8" : "#E0B978", flexShrink: 0 }}>{r.who}</span>
                </div>
              ))}
            </div>
          </ChatBubble>
        </ChatRow>
      );
    case "timeline":
      return (
        <ChatRow>
          <ChatBubble pad="0">
            <div style={{ width: 420, background: TGC.panel, borderRadius: 12, overflow: "hidden", border: `1px solid ${TGC.hair}` }}>
              <div style={{ padding: "8px 13px", borderBottom: `1px solid ${TGC.hair}`, fontSize: 12, fontWeight: 700, color: TGC.link, direction: "rtl", textAlign: "right" }}>{m.head}</div>
              <div style={{ padding: "10px 13px", direction: "rtl" }}>
                {m.rows.map((r: any, j: number) => (
                  <div key={j} style={{ display: "flex", gap: 11, paddingBottom: j < m.rows.length - 1 ? 10 : 0 }}>
                    <span style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, paddingTop: 4 }}>
                      <span style={{ width: 6, height: 6, borderRadius: 999, background: r.tone ?? TGC.accent }} />
                      {j < m.rows.length - 1 && <span style={{ width: 1, flex: 1, background: TGC.hair, marginTop: 3 }} />}
                    </span>
                    <span style={{ fontFamily: CHAT_MONO, fontSize: 10, color: TGC.dim, flexShrink: 0, minWidth: 54, textAlign: "right" }}>{r.when}</span>
                    <span style={{ fontSize: 11.5, color: TGC.text, lineHeight: 1.5, textAlign: "right" }}>{r.what}</span>
                  </div>
                ))}
              </div>
            </div>
          </ChatBubble>
        </ChatRow>
      );
    case "buttons":
      return (
        <ChatRow>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", maxWidth: "88%" }}>
            {m.items.map((b: any, j: number) => (
              <span key={j} style={{ fontSize: 11.5, fontWeight: 600, color: b.primary ? "#0E1621" : TGC.link, background: b.primary ? "#8FD8B8" : "rgba(82,136,193,0.16)", border: `1px solid ${b.primary ? "#8FD8B8" : "rgba(82,136,193,0.4)"}`, borderRadius: 8, padding: "6px 13px", direction: "rtl" }}>{b.l}</span>
            ))}
          </div>
        </ChatRow>
      );
    case "system":
      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <span style={{ fontSize: 11, color: "#8FD8B8", background: "rgba(63,143,111,0.14)", borderRadius: 999, padding: "4px 13px", direction: "rtl" }}>{m.t}</span>
        </div>
      );
    default:
      return null;
  }
}

type ChatSlideProps = {
  n?: string; cat?: string; chipVariant?: ChipVariant; title: string;
  trigger?: string; said?: string; msgs?: Record<string, any>[];
  outcome?: string; writes?: string; guard?: string;
};

function ChatSlide({ n, cat, chipVariant = "default", title, trigger, said, msgs = [], outcome, writes, guard }: ChatSlideProps) {
  return (
    <section data-slide="chat" style={{ ...slideContainerStyle, flexDirection: "row", alignItems: "stretch", gap: "3vw", padding: "5vh 5vw 9vh" }}>
      <div style={{ flex: "0 0 40%", display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {n && <span style={{ width: 34, height: 34, borderRadius: 10, background: chipStyle(chipVariant).color as string, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, flexShrink: 0 }}>{n}</span>}
          {cat && <span style={{ ...chipStyle(chipVariant), margin: 0 }}>{cat}</span>}
        </div>
        <h2 style={{ ...slideTitleStyle, marginTop: 20 }}>{renderTitleLines(title)}</h2>
        {trigger && <p style={{ ...slideLedeStyle, marginTop: 14 }}>{trigger}</p>}
        {said && (
          <div style={{ marginTop: 18, fontSize: 13, lineHeight: 1.6, color: T.ink3, borderInlineStart: `3px solid ${T.line}`, paddingInlineStart: 14, direction: "rtl", textAlign: "right" }}>{said}</div>
        )}
        <div style={{ marginTop: "auto", paddingTop: 22, borderTop: `1px solid ${T.line}`, display: "flex", flexDirection: "column", gap: 7, fontSize: 12.5, lineHeight: 1.5, color: T.ink2 }}>
          {outcome && <div><b style={{ color: T.green }}>OUTCOME · </b>{outcome}</div>}
          {writes && <div><b style={{ color: T.accent }}>WRITES · </b>{writes}</div>}
          {guard && <div><b style={{ color: T.amber }}>GUARDRAIL · </b>{guard}</div>}
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", background: TGC.bg, borderRadius: 18, overflow: "hidden", border: `1px solid ${TGC.hair}`, boxShadow: "0 20px 60px rgba(15,23,32,0.22)" }}>
        <div style={{ height: 48, background: TGC.head, display: "flex", alignItems: "center", gap: 11, padding: "0 16px", flexShrink: 0 }}>
          <span style={{ width: 30, height: 30, borderRadius: 999, background: "#2E6B52", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "#fff" }}>F</span>
          <span>
            <span style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: "#FFF" }}>Falcon</span>
            <span style={{ display: "block", fontSize: 10, color: "#8FD8B8" }}>online</span>
          </span>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, padding: "14px 16px 16px" }}>
          {msgs.map((m, i) => <ChatMessage key={i} m={m} />)}
        </div>
      </div>
    </section>
  );
}

// ── BlockConfig ───────────────────────────────────────────────────────────
export const pitchDeckConfig: BlockConfig = {
  components: {
    Deck,
    ChatSlide,
    DiagramSlide,
    CoverSlide,
    ArchitectureSlide,
    UseCaseSlide,
    ContentSlide,
    CompareSlide,
    QuoteSlide,
    StackSlide,
    ImageSlide,
    FlowDiagramSlide,
    WorkflowSlide,
    ExecSummarySlide,
    CostSimSlide,
  } as unknown as BlockConfig["components"],
};
